import {HoneycombWebSDK} from '@honeycombio/opentelemetry-web';
import {getWebAutoInstrumentations} from '@opentelemetry/auto-instrumentations-web';

import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {ImperativeContextManager} from 'common/util/tracing';

import {
  applyQueryHeaderAttributesToFetchSpan,
  applyQueryHeaderAttributesToXhrSpan,
  FetchSpanWrapperProcessor,
  HttpUrlPathProcessor,
} from './honeycomb-span-processors';
import {detectBrowserAutomation} from './detect-browser-automation';

const configDefaults = {
  ignoreNetworkEvents: true,
  propagateTraceHeaderCorsUrls: [
    // adds span trace ids to all request on mixpanel domains
    /^https:\/\/(.+\.)?mixpanel\.(com|org)/g,
  ],
  ignoreUrls: [
    // Specific first-party URLs we want to ignore
    /mixpanel\.(com|org)\/tproxy/,
    // Allow list of domains we care about
    /^(?!.*(mixpanel\.(com|org)|mxpnl\.com|web-internal|storage\.googleapis\.com|zendesk\.com)).*$/,
  ],
};

// Honeycomb Core Web Vitals are high volume and we don't really use them
let recordCoreWebVitalsPercent = 10;
// Don't record web vitals for automated browsers like screenshot server
// - The compute resources available to an automated runner are not representative of a real user
// - It's just noise
if (detectBrowserAutomation({userAgent: window?.navigator?.userAgent, host: window?.location?.host})) {
  recordCoreWebVitalsPercent = 0;
}
const shouldRecordCoreWebVitals = recordCoreWebVitalsPercent > 0 && Math.random() * 100 <= recordCoreWebVitalsPercent;

/**
 * @see https://docs.honeycomb.io/send-data/javascript-browser/honeycomb-distribution/
 */
const sdk = new HoneycombWebSDK({
  /** set debug & localVisualizations to true for local debugging on traces */
  debug: false,
  localVisualizations: false,
  apiKey: HONEYCOMB_TOKEN,
  serviceName: `web-client`,
  instrumentations: [
    getWebAutoInstrumentations({
      '@opentelemetry/instrumentation-document-load': {
        ...configDefaults,
        ignorePerformancePaintEvents: !shouldRecordCoreWebVitals,
      },
      '@opentelemetry/instrumentation-fetch': {
        ...configDefaults,
        applyCustomAttributesOnSpan: applyQueryHeaderAttributesToFetchSpan,
      },
      '@opentelemetry/instrumentation-user-interaction': {enabled: false},
      '@opentelemetry/instrumentation-xml-http-request': {
        ...configDefaults,
        applyCustomAttributesOnSpan: applyQueryHeaderAttributesToXhrSpan,
      },
    }),
  ],
  webVitalsInstrumentationConfig: {
    enabled: shouldRecordCoreWebVitals,
  },
  resourceAttributes: {
    user_id: userCookieUtils.getUserIdentityFromCookie()?.id,
    bundler: process.env.BUNDLER,
  },
  contextManager: new ImperativeContextManager(),
  spanProcessors: [new HttpUrlPathProcessor(), new FetchSpanWrapperProcessor()],
});
sdk.start();
